
import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ProductVariant } from '@/types/product';
import { useAuth } from './AuthContext';

export interface CartItem {
  id: string;
  name: string;
  variant: ProductVariant;
  quantity: number;
  price: number;
  image: string;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: CartItem) => boolean;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, change: number) => void;
  clearCart: () => void;
  isUserLoggedIn: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const [items, setItems] = useState<CartItem[]>([]);

  // Charger le panier depuis localStorage quand l'utilisateur change
  useEffect(() => {
    if (user) {
      const cartKey = `agroref_cart_${user.id}`;
      const savedCart = localStorage.getItem(cartKey);
      if (savedCart) {
        try {
          setItems(JSON.parse(savedCart));
        } catch (error) {
          console.error('Error parsing cart:', error);
          setItems([]);
        }
      } else {
        setItems([]);
      }
    } else {
      // Si pas d'utilisateur connecté, vider le panier
      setItems([]);
    }
  }, [user]);

  // Sauvegarder le panier dans localStorage quand il change
  useEffect(() => {
    if (user) {
      const cartKey = `agroref_cart_${user.id}`;
      localStorage.setItem(cartKey, JSON.stringify(items));
    }
  }, [items, user]);

  const addItem = (newItem: CartItem): boolean => {
    // Vérifier si l'utilisateur est connecté
    if (!user) {
      return false;
    }

    if (!newItem.id || !newItem.name || !newItem.variant || !newItem.price) {
      return false;
    }

    setItems(currentItems => {
      const existingItemIndex = currentItems.findIndex(
        item => item.id === newItem.id && 
               item.variant.size === newItem.variant.size && 
               item.variant.colorName === newItem.variant.colorName
      );

      if (existingItemIndex > -1) {
        const updatedItems = [...currentItems];
        updatedItems[existingItemIndex].quantity += newItem.quantity;
        return updatedItems;
      }

      return [...currentItems, newItem];
    });
    
    return true;
  };

  const removeItem = (itemId: string) => {
    if (!itemId) return;
    setItems(currentItems => currentItems.filter(item => item.id !== itemId));
  };

  const updateQuantity = (itemId: string, change: number) => {
    if (!itemId) return;

    setItems(currentItems =>
      currentItems.map(item =>
        item.id === itemId
          ? { ...item, quantity: Math.max(1, item.quantity + change) }
          : item
      )
    );
  };

  const clearCart = () => setItems([]);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart, isUserLoggedIn: !!user }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
